/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import ChatDrawer from '../../components/search-chat/chat';
import { useGetWidgetConfigQuery } from '../../store/widget/widgetConfigApi';
import { useGetCurrentUserQuery } from '../../store/user/userApi';
import { classNames } from '../../utils/classNames';

const ASSET_BASE = '/new-website';
const HOTEL_ROUTE = '/noctyra-hotels';
const BLOG_ROUTE = `${HOTEL_ROUTE}/blog/us-open-this-august-in-new-york`;
const WS_URL = process.env.REACT_APP_WS_URL;
const WS_PATH = process.env.REACT_APP_WS_PATH;
const SOCKET_CLIENT_ID = 'demo-client';
const DEFAULT_WIDGET_CLIENT_ID = 'd2957434-b0c1-7049-5d57-d6f3e5695d30';

type SocketInboundMessage = {
	messageId?: string;
	from?: string;
	text?: string;
	timestamp?: string;
};

type ArticlePreview = {
	image: string;
	tag: string;
	date: string;
	readTime: string;
	title: string;
	description: string;
	href?: string;
};

const hotelNews: ArticlePreview[] = [
	{
		image: `${ASSET_BASE}/us-open-blog.png`,
		tag: 'LOCAL EVENTS',
		date: 'July 28, 2026',
		readTime: '9 min read',
		title: 'Experience the US Open This August in New York',
		description:
			'Experience the thrill of the US Open with exclusive hotel and ticket packages at Noctyra Hotels.',
		href: BLOG_ROUTE,
	},
	{
		image: `${ASSET_BASE}/behind-the-scene-blog.jpg`,
		tag: 'HOTEL DESIGN',
		date: 'April 10, 2026',
		readTime: '6 min read',
		title: "Behind the Scenes: The Making of Noctyra's Signature Suites",
		description:
			'Take a tour of our signature suites, where innovative design meets unparalleled comfort and luxury.',
	},
	{
		image: `${ASSET_BASE}/events-near-blog.jpg`,
		tag: 'LOCAL EVENTS',
		date: 'March 20, 2026',
		readTime: '5 min read',
		title: 'Top Local Events Near Noctyra Hotel This Spring',
		description:
			"From cultural festivals to outdoor concerts, find out what's happening near Noctyra Hotel this spring.",
	},
];

const navItems = [
	{ label: 'Hotel', hash: '#hotel' },
	{ label: 'Rooms', hash: '#rooms' },
	{ label: 'Restaurant', hash: '#restaurant' },
	{ label: 'Booking', hash: '#booking' },
	{ label: 'Blogs', hash: '#blogs' },
];

const NoctyraHeader: React.FC<{
	active?: string;
	showUtility?: boolean;
}> = ({ active = '', showUtility = true }) => {
	const [menuOpen, setMenuOpen] = useState(false);

	return (
		<header className="bg-white px-6 pt-7 md:px-16">
			<div className="mx-auto max-w-[1400px]">
				<div className="flex items-center justify-between gap-6">
					<Link to={HOTEL_ROUTE} className="shrink-0" aria-label="Noctyra Hotels home">
						<img
							src={`${ASSET_BASE}/Noctyra-Hotels-logo.svg`}
							alt="Noctyra Hotels"
							className="h-[19px] w-auto"
						/>
					</Link>
					<nav className="hidden items-center gap-6 text-sm text-[#171717] md:flex">
						{navItems.map((item) => (
							<Link
								key={item.label}
								to={`${HOTEL_ROUTE}${item.hash}`}
								className={classNames(
									'transition-opacity hover:opacity-70',
									active === item.label ? 'font-semibold' : 'font-normal',
								)}
							>
								{item.label}
							</Link>
						))}
					</nav>
					{showUtility ? (
						<div className="hidden items-center gap-5 text-sm text-[#171717] md:flex">
							<a href="#contacts" className="transition-opacity hover:opacity-70">
								Contact
							</a>
							<span>EN</span>
						</div>
					) : (
							<div className="hidden w-[96px] md:block" />
						)}
						<button
							type="button"
							aria-label={menuOpen ? 'Close menu' : 'Open menu'}
							aria-expanded={menuOpen}
							onClick={() => setMenuOpen((prev) => !prev)}
							className="relative flex h-9 w-9 items-center justify-center rounded-full border border-[#A4A7AE] transition hover:shadow-sm md:hidden"
						>
							<span
								className={classNames(
									'absolute h-[1.5px] w-4 rounded-full bg-[#171717] transition',
									menuOpen ? 'rotate-45' : '-translate-y-1.5',
								)}
							/>
							<span
								className={classNames(
									'absolute h-[1.5px] w-4 rounded-full bg-[#171717] transition',
									menuOpen ? 'opacity-0' : 'opacity-100',
								)}
							/>
							<span
								className={classNames(
									'absolute h-[1.5px] w-4 rounded-full bg-[#171717] transition',
									menuOpen ? '-rotate-45' : 'translate-y-1.5',
								)}
							/>
						</button>
					</div>
					<div className="mt-6 border-b border-[#252B37] md:mt-5" />
					{menuOpen && (
						<nav className="grid gap-4 border-b border-[#E5E5E5] py-5 text-sm text-[#171717] md:hidden">
							{navItems.map((item) => (
								<Link
									key={item.label}
									to={`${HOTEL_ROUTE}${item.hash}`}
									onClick={() => setMenuOpen(false)}
									className={classNames(
										'transition-opacity hover:opacity-70',
										active === item.label ? 'font-semibold' : 'font-normal',
									)}
								>
									{item.label}
								</Link>
							))}
							{showUtility && (
								<a
									href="#contacts"
									onClick={() => setMenuOpen(false)}
									className="transition-opacity hover:opacity-70"
								>
									Contact
								</a>
							)}
							{showUtility && <span>EN</span>}
						</nav>
					)}
				</div>
			</header>
	);
};

const SearchBox: React.FC<{
	icon: string;
	label: string;
}> = ({ icon, label }) => (
	<div className="flex min-h-[58px] items-center gap-3 rounded-lg border border-white/55 bg-black/25 px-5 text-sm text-white backdrop-blur-md">
		<span className="flex h-5 w-5 items-center justify-center text-base" aria-hidden="true">
			{icon}
		</span>
		<span>{label}</span>
	</div>
);

const SectionArrowControls: React.FC = () => (
	<div className="flex items-center gap-3">
		<button
			type="button"
			aria-label="Previous"
			className="h-8 w-8 rounded-full bg-white transition hover:scale-105 hover:shadow-md active:scale-95 md:h-10 md:w-10"
		>
			<img src={`${ASSET_BASE}/left-array.svg`} alt="" className="h-full w-full" />
		</button>
		<button
			type="button"
			aria-label="Next"
			className="h-8 w-8 rounded-full bg-white transition hover:scale-105 hover:shadow-md active:scale-95 md:h-10 md:w-10"
		>
			<img src={`${ASSET_BASE}/right-array.svg`} alt="" className="h-full w-full" />
		</button>
	</div>
);

const RoomMeta: React.FC<{
	rating?: string;
	title: string;
}> = ({ rating, title }) => (
	<div className="mt-4 text-[#171717]">
		{rating && <p className="mb-2 text-xs">{rating}</p>}
		<h3 className="text-base font-semibold">{title}</h3>
		<p className="mt-1 text-xs text-[#69707A]">1 bed | 2 sleeps</p>
	</div>
);

const NewsCard: React.FC<{
	article: ArticlePreview;
}> = ({ article }) => {
	const content = (
		<article className="group h-full">
			<div className="relative overflow-hidden rounded-lg">
				<img
					src={article.image}
					alt={article.title}
					className="h-[270px] w-full object-cover brightness-75 transition-transform duration-300 group-hover:scale-[1.02] md:h-[300px]"
				/>
				<span className="absolute left-5 top-5 rounded-full bg-white px-5 py-2 text-xs font-semibold text-[#073033]">
					{article.tag}
				</span>
			</div>
			<div className="mt-7">
				<p className="text-sm text-[#6F7378]">
					{article.date}
					<span className="mx-4">•</span>
					{article.readTime}
				</p>
				<h3 className="mt-4 text-lg font-semibold leading-tight text-[#171717] md:text-xl">
					{article.title}
				</h3>
				<p className="mt-4 text-sm leading-6 text-[#686868] md:text-base">{article.description}</p>
				<span className="mt-6 inline-flex items-center gap-3 text-base font-semibold text-[#073033]">
					Read More
					<span aria-hidden="true">›</span>
				</span>
			</div>
		</article>
	);

	if (!article.href) return content;

	return (
		<Link to={article.href} className="block h-full">
			{content}
		</Link>
	);
};

const NoctyraFooter: React.FC = () => (
	<footer id="contacts" className="bg-white px-6 py-14 md:px-16 md:py-20">
		<div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-[1.2fr_1fr_1.2fr]">
			<div>
				<h2 className="text-3xl font-semibold text-[#171717]">For Enquiry</h2>
				<p className="mt-4 max-w-[260px] text-lg leading-6 text-[#171717]">
					Book your trip in a minute, experience once in a lifetime.
				</p>
				<div className="mt-20 max-w-[270px] border-b border-[#171717] pb-1">
					<div className="flex items-center justify-between text-sm text-[#9A9A9A]">
						<span>enter your email</span>
						<button type="button" className="text-base text-[#171717]">
							ok
						</button>
					</div>
				</div>
				<p className="mt-3 text-xs text-[#9A9A9A]">
					By clicking, you agree to the{' '}
					<a href="#privacy" className="text-[#171717] underline">
						privacy policy
					</a>
				</p>
			</div>

			<div className="grid grid-cols-2 gap-10 text-sm text-[#171717]">
				<div>
					<h3 className="font-medium">Information</h3>
					<div className="mt-3 space-y-2 text-[#8E8E8E]">
						<p>Hotels</p>
						<p>Rooms</p>
						<p>Restaurant</p>
						<p>Data Policy</p>
						<p>Offer</p>
					</div>
				</div>
				<div>
					<h3 className="font-medium">Contacts</h3>
					<div className="mt-3 space-y-2 text-[#8E8E8E]">
						<p>noctyra@email.com</p>
						<p>+1 (123) 12-345-6789</p>
					</div>
				</div>
				<div className="col-span-2 mt-14 flex flex-wrap items-center gap-12 text-[#171717]">
					<span>Whatsapp</span>
					<span>Instagram</span>
					<span>Telegram</span>
				</div>
			</div>

			<div className="flex items-end justify-start lg:justify-end">
				<div className="lg:text-right">
					<img
						src={`${ASSET_BASE}/Noctyra-Hotels-logo.svg`}
						alt="Noctyra Hotels"
						className="h-[23px] w-auto lg:ml-auto"
					/>
					<p className="mt-5 text-base text-[#5F7676]">Experience worth living for.</p>
				</div>
			</div>
		</div>
	</footer>
);

const NoctyraChatWidget: React.FC<{
	isArticle?: boolean;
}> = ({ isArticle = false }) => {
	const [open, setOpen] = useState(false);
	const [showHint, setShowHint] = useState(false);
	const [socketMessages, setSocketMessages] = useState<any[]>([]);
	const location = useLocation();
	const socketRef = useRef<Socket | null>(null);
	const joinedRef = useRef(false);
	const conversationIdRef = useRef('');
	const hasAccessToken =
		typeof window !== 'undefined' &&
		!!window.localStorage.getItem('accessToken');
	const { data: currentUser } = useGetCurrentUserQuery(undefined, {
		skip: !hasAccessToken,
	});
	const searchParams = new URLSearchParams(location.search);
	const scriptAgentId = searchParams.get('agentId') || 'claire';
	const scriptClientId = searchParams.get('clientId');
	const widgetClientId =
		scriptClientId ||
		currentUser?.data?.companyData?.sub ||
		DEFAULT_WIDGET_CLIENT_ID;
	const { data: widgetConfig } = useGetWidgetConfigQuery(
		{ agentId: scriptAgentId, clientId: widgetClientId },
		{ skip: !widgetClientId },
	);

	const toggleAvatar = useMemo(() => {
		return `/assets/agents/${scriptAgentId.toLowerCase()}-agent.svg`;
	}, [scriptAgentId, widgetConfig]);
	const agentDisplayName = useMemo(() => {
		const raw = widgetConfig?.agent || scriptAgentId || 'claire';
		const normalized = String(raw).replace(/agent-|_/g, ' ').trim();
		return normalized
			? normalized.charAt(0).toUpperCase() + normalized.slice(1)
			: 'claire';
	}, [widgetConfig, scriptAgentId]);
	const chatHintText = isArticle
		? 'Hope you are having a good read, Would you like to check availability dates for Noctyra Hotel?'
		: `Hi, I am ${agentDisplayName}, is there anything I can help you with?`;

	const toggleChat = () => {
		setOpen((prev) => !prev);
	};

	useEffect(() => {
		if (!open) return undefined;

		const conversationStorageKey = 'widget_conversation_id';
		let localConversationId = localStorage.getItem(conversationStorageKey);
		if (!localConversationId) {
			localConversationId = `conv-${Date.now()}`;
			localStorage.setItem(conversationStorageKey, localConversationId);
		}
		conversationIdRef.current = localConversationId;
		joinedRef.current = false;

		const socket = io(WS_URL, {
			path: WS_PATH,
			transports: ['websocket'],
			autoConnect: false,
			auth: { clientId: SOCKET_CLIENT_ID, agentId: scriptAgentId },
		});

		const mapMessage = (msg: SocketInboundMessage) => {
			if (msg?.from === 'agent') {
				return {
					agent: {
						_id: msg.messageId,
						message: msg.text || '',
						createdAt: msg.timestamp || new Date().toISOString(),
						files: [],
					},
				};
			}
			return {
				user: {
					message: msg.text || '',
					createdAt: msg.timestamp || new Date().toISOString(),
					files: [],
				},
			};
		};

		const onMessageNew = (msg: SocketInboundMessage) => {
			if (msg?.from !== 'agent') return;
			const normalized = mapMessage(msg);
			setSocketMessages((prev) => {
				const id = normalized?.agent?._id;
				if (id && prev.some((m: any) => m?.agent?._id === id)) return prev;
				return [...prev, normalized];
			});
		};

		const onConnect = async () => {
			try {
				const res = await socket.emitWithAck('conversation:join', {
					conversationId: localConversationId,
				});
				joinedRef.current = !!res?.ok;
			} catch (e) {
				console.error('socket join failed', e);
				joinedRef.current = false;
			}
		};

		const onDisconnect = () => {
			joinedRef.current = false;
		};

		const onSocketError = (payload: any) => {
			console.error('socket error:', payload);
		};

		socket.on('connect', onConnect);
		socket.on('disconnect', onDisconnect);
		socket.on('error', onSocketError);
		socket.on('message:new', onMessageNew);
		socket.connect();
		socketRef.current = socket;

		return () => {
			socket.off('connect', onConnect);
			socket.off('disconnect', onDisconnect);
			socket.off('error', onSocketError);
			socket.off('message:new', onMessageNew);
			socket.disconnect();
			socketRef.current = null;
			joinedRef.current = false;
		};
	}, [open, scriptAgentId]);

	const onSendSocketMessage = async (payload: { text: string }) => {
		const socket = socketRef.current;
		const conversationId = conversationIdRef.current;
		if (!payload?.text?.trim()) return;
		if (!socket || !socket.connected || !joinedRef.current || !conversationId) {
			throw new Error('Socket not connected yet. Please try again.');
		}
		const messageId = `m-${Date.now()}-${Math.random().toString(16).slice(2)}`;
		const ack = await socket.emitWithAck('message:send', {
			conversationId,
			messageId,
			from: 'user',
			text: payload.text.trim(),
			timestamp: new Date().toISOString(),
			metadata: { ui: 'widget' },
		});
		if (!ack?.ok) {
			throw new Error(ack?.error || 'Socket send failed');
		}
	};

	useEffect(() => {
		const timer = setTimeout(() => setShowHint(true), 2000);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (open) setShowHint(false);
	}, [open]);

	useEffect(() => {
		try {
			if (window.parent && window.parent !== window) {
				window.parent.postMessage(
					{ type: 'hotelworld-widget-toggle', open },
					'*',
				);
			}
		} catch (e) {
			// noop
		}
	}, [open]);

	return (
		<>
			<AnimatePresence>
				{open && (
					<motion.button
						type="button"
						aria-label="Close chat overlay"
						onClick={() => setOpen(false)}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 z-30 bg-black/35 backdrop-blur-[1px]"
					/>
				)}
			</AnimatePresence>
			<ChatDrawer
				isOpen={open}
				triggerId="hotelworld-chat-widget"
				historyId={undefined}
				action={undefined}
				chatData={socketMessages}
				variant="widget"
				agentId={scriptAgentId}
				clientId={widgetClientId}
				onSendSocketMessage={onSendSocketMessage}
				onClose={() => setOpen(false)}
			/>

			<AnimatePresence>
				{showHint && !open && (
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 8 }}
						transition={{ duration: 0.3 }}
						className="fixed bottom-24 right-6 z-50 hidden rounded-2xl bg-gray-100 px-4 py-2 text-sm font-normal text-black shadow md:block"
					>
						{chatHintText}
					</motion.div>
				)}
			</AnimatePresence>
			<button
				type="button"
				onClick={toggleChat}
				className={`fixed bottom-6 right-6 z-50 items-center gap-3 rounded-full bg-transparent text-xs font-medium md:pl-28 ${
					open ? 'hidden' : 'flex'
				}`}
			>
				<span
					className={`mr-2 hidden rounded-full py-1 text-[13px] font-normal text-white mix-blend-difference md:flex ${
						open ? 'md:hidden' : ''
					}`}
				>
					Powered by HotelWorld AI
				</span>
				<span
					className={`h-[60px] w-[60px] items-center justify-center rounded-full bg-white shadow-xl transition-all md:flex ${
						open ? 'hidden' : 'flex rounded-br-md bg-white'
					}`}
				>
					<img
						src={toggleAvatar}
						alt="Open chat"
						className="h-10 w-10 rounded-full border border-black bg-white object-cover"
					/>
				</span>
			</button>
		</>
	);
};

export const NoctyraHotelPage: React.FC = () => {
	const location = useLocation();

	useEffect(() => {
		if (!location.hash) return;
		const id = location.hash.replace('#', '');
		window.setTimeout(() => {
			document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
		}, 0);
	}, [location.hash]);

	return (
		<div className="min-h-screen bg-white font-inter text-[#171717]">
			<NoctyraHeader />
			<main>
				<section id="hotel" className="bg-white">
					<div className="mx-auto max-w-[1400px] px-6 py-10 md:px-16 md:py-15">
						<h1 className="text-center text-3xl font-light leading-tight text-[#171717] md:text-[70px] md:leading-none">
							Welcome to Noctyra
						</h1>
					</div>
					<div
						id="booking"
						className="relative flex min-h-[640px] items-end justify-center overflow-hidden text-white md:min-h-[560px]"
					>
						<img
							src={`${ASSET_BASE}/header.png`}
							alt="Noctyra hotel room overlooking New York"
							className="absolute inset-0 h-full w-full object-cover"
						/>
						<div className="absolute inset-0 bg-black/35" />
						<p className="absolute left-1/2 top-12 z-10 max-w-[520px] -translate-x-1/2 px-6 text-center text-base leading-7 text-white/90 md:top-[28%]">
							Welcome to our Cozy Haven, where comfort meets style and exceptional service that elevates any experience
						</p>
						<div className="relative z-10 mb-10 w-full max-w-[1020px] px-6 md:mb-20">
							<div className="grid grid-cols-1 gap-4 md:grid-cols-4">
								<SearchBox icon="⌕" label="Search by location" />
								<SearchBox icon="□" label="Check in / Check out" />
								<SearchBox icon="♙" label="Guests" />
								<button
									type="button"
									className="min-h-[58px] rounded-full bg-white px-8 text-base font-semibold text-[#171717] transition-opacity hover:opacity-90"
								>
									Book Now
								</button>
							</div>
						</div>
					</div>
				</section>

				<section id="rooms" className="bg-white px-6 py-20 md:px-16 md:py-28">
					<div className="mx-auto max-w-[1400px]">
						<div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
							<h2 className="text-2xl font-light leading-none text-[#171717] md:text-[50px]">
								Our Rooms
							</h2>
							<SectionArrowControls />
						</div>

						<div className="mt-14 grid gap-8 lg:grid-cols-[2fr_0.85fr_0.85fr] lg:items-start">
							<article className="lg:row-span-2">
								<img
									src={`${ASSET_BASE}/standard-room.png`}
									alt="Standard Room"
									className="aspect-[1.02/1] w-full object-cover"
								/>
								<RoomMeta rating="4.6 Ratings" title="Standard Room" />
							</article>

							<article>
								<img
									src={`${ASSET_BASE}/junior-room.png`}
									alt="Junior Suite bathroom and city view"
									className="aspect-square w-full object-cover"
								/>
								<RoomMeta title="Junior Suite" />
							</article>

							<aside className="flex aspect-square flex-col justify-between bg-[#E8E8E8] p-7">
								<p className="text-base leading-5 text-[#333333]">
									The hotel offers different room categories that are suitable for both business travelers and vacationers.
								</p>
								<button
									type="button"
									className="mx-auto mb-3 mt-8 min-h-[58px] w-full max-w-[220px] rounded-full bg-[#101010] px-8 text-base font-medium text-white transition-opacity hover:opacity-90"
								>
									Book Now
								</button>
							</aside>

							<article className="lg:col-start-3">
								<img
									src={`${ASSET_BASE}/junior-suite.png`}
									alt="Junior Suite bedroom with skyline view"
									className="aspect-square w-full object-cover"
								/>
								<RoomMeta rating="4.6 Ratings" title="Junior Suite" />
							</article>
						</div>
					</div>
				</section>

				<section id="restaurant" className="bg-white px-6 pb-24 md:px-16 md:pb-36">
					<div className="mx-auto grid max-w-[1110px] gap-8 lg:grid-cols-2">
						<div className="flex min-h-[520px] flex-col justify-between bg-[#E8E8E8] p-8 md:p-12">
							<div>
								<h2 className="text-5xl font-normal leading-tight text-[#171717] md:text-[56px]">
									RESTAURANT
									<br />
									AT THE HOTEL
								</h2>
								<p className="mt-6 max-w-[410px] text-base leading-5 text-[#333333]">
									Our chefs offer both international and local dishes, prepared only with fresh ingredients. In the cozy atmosphere of the restaurant, you can enjoy not only delicious food but also pleasant music and a friendly ambiance.
								</p>
							</div>
							<button
								type="button"
								className="min-h-[58px] w-full max-w-[220px] rounded-full bg-[#101010] px-8 text-base font-medium text-white transition-opacity hover:opacity-90"
							>
								Reservation
							</button>
						</div>

						<div className="relative min-h-[520px] overflow-hidden">
							<img
								src={`${ASSET_BASE}/restaurant-at-hotel.png`}
								alt="Restaurant at Noctyra Hotel"
								className="h-full w-full object-cover"
							/>
							<button
								type="button"
								aria-label="Previous restaurant image"
								className="absolute left-4 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full bg-white shadow-sm transition hover:scale-105 hover:shadow-lg active:scale-95 md:h-12 md:w-12"
							>
								<img src={`${ASSET_BASE}/left-array.svg`} alt="" className="h-full w-full" />
							</button>
							<button
								type="button"
								aria-label="Next restaurant image"
								className="absolute right-4 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full bg-white shadow-sm transition hover:scale-105 hover:shadow-lg active:scale-95 md:h-12 md:w-12"
							>
								<img src={`${ASSET_BASE}/right-array.svg`} alt="" className="h-full w-full" />
							</button>
						</div>
					</div>
				</section>

				<section className="relative h-[560px] overflow-hidden bg-[#171717] md:h-[760px]">
					<video
						className="h-full w-full object-cover"
						src={`${ASSET_BASE}/14776854_3840_2160_24fps.mp4`}
						autoPlay
						muted
						loop
						playsInline
						preload="metadata"
					/>
					<div className="absolute inset-0 bg-black/15" />
					<h2 className="absolute left-8 top-16 max-w-[520px] text-5xl font-light leading-tight text-white md:left-24 md:text-[56px]">
						VISUALIZE OUR MODERN HOTEL
					</h2>
				</section>

				<section id="blogs" className="bg-white px-6 py-20 md:px-16 md:py-28">
					<div className="mx-auto max-w-[1500px]">
						<div className="text-center">
							<h2 className="text-4xl font-semibold text-[#171717]">HOTEL NEWS</h2>
							<p className="mt-6 text-xl text-[#666666]">
								Stay informed about the latest trends, openings, and travel tips.
							</p>
						</div>
						<div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-3">
							{hotelNews.map((article) => (
								<NewsCard key={article.title} article={article} />
							))}
						</div>
					</div>
				</section>
			</main>
			<NoctyraFooter />
			<NoctyraChatWidget />
		</div>
	);
};

const ArticleSection: React.FC<{
	title: string;
	children: React.ReactNode;
}> = ({ title, children }) => (
	<section className="space-y-9">
		<h2 className="text-2xl font-semibold leading-tight text-[#073033] md:text-3xl">
			{title}
		</h2>
		<div className="space-y-7 text-base leading-8 text-[#4B4B4B] md:text-lg md:leading-8">
			{children}
		</div>
	</section>
);

export const NoctyraBlogArticlePage: React.FC = () => {
	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	}, []);

	return (
		<div className="min-h-screen bg-white font-inter text-[#171717]">
			<NoctyraHeader active="Blogs" showUtility={false} />
			<main>
				<section className="relative min-h-[460px] overflow-hidden text-white md:min-h-[500px]">
					<img
						src={`${ASSET_BASE}/us-open-blog.png`}
						alt="US Open stadium in New York"
						className="absolute inset-0 h-full w-full object-cover"
					/>
					<div className="absolute inset-0 bg-black/55" />
					<div className="relative z-10 mx-auto flex min-h-[460px] max-w-[1400px] flex-col justify-between px-6 py-16 md:min-h-[500px] md:px-24">
						<div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
							<p className="text-lg text-white/90">
								July 28, 2026
								<span className="mx-6">•</span>
								9 min read
							</p>
							<span className="w-fit rounded-full bg-white px-7 py-4 text-base mb-3 md:mb-0 !text-sm font-semibold text-[#073033]">
								TRAVEL GUIDE
							</span>
						</div>
						<h1 className="max-w-[820px] text-5xl font-semibold leading-tight text-white md:text-[64px]">
							Experience the US Open This August in New York
						</h1>
					</div>
				</section>

					<article className="mx-auto max-w-[920px] space-y-16 px-6 py-20 md:py-24">
						<div className="space-y-10 text-base leading-8 text-[#4B4B4B] md:text-lg md:leading-8">
						<p>
							August in New York carries a special kind of energy—and when tennis comes to town, it becomes electric. As the city warms up for the final Grand Slam of the year, the US Open Tennis Championships transforms New York into a global stage of sport, style, and late-summer excitement.
						</p>
						<p>
							Whether you’re a lifelong tennis fan or simply curious to experience one of the city’s most iconic events, staying with us puts you right at the heart of it all.
						</p>
					</div>

					<ArticleSection title="A Grand Slam Like No Other">
						<p>
							Held annually in Queens at the USTA Billie Jean King National Tennis Center, the US Open is famous for its electric night matches, passionate crowds, and dramatic moments under the lights of Arthur Ashe Stadium.
						</p>
						<div>
							<p>Late August marks the beginning of the tournament atmosphere:</p>
							<ul className="mt-6 list-disc space-y-2 pl-8">
								<li>Qualifying rounds bring rising stars and thrilling, up-close matches</li>
								<li>Practice sessions allow fans to see top players preparing for battle</li>
								<li>The city buzzes as athletes, media, and fans from around the world arrive</li>
							</ul>
						</div>
						<p>It’s tennis, New York–style: bold, loud, and unforgettable.</p>
					</ArticleSection>

					<ArticleSection title="Why August Is the Perfect Time to Visit">
						<p>Beyond the courts, August is one of the most vibrant months in New York City:</p>
						<div className="space-y-1">
							<p>Long summer evenings perfect for rooftop dining</p>
							<p>Outdoor concerts, pop-up events, and waterfront walks</p>
							<p>A festive international crowd drawn by the US Open and summer travel</p>
						</div>
						<p>
							You can spend your day exploring museums or neighborhoods, then head to a thrilling night match—or simply enjoy the buzz from across the city.
						</p>
					</ArticleSection>

					<ArticleSection title="Getting There & Getting Comfortable">
						<p>
							The US Open venue in Queens is easily accessible by subway or car, making it simple to combine match days with sightseeing. After an exciting day courtside, returning to a calm, comfortable hotel makes all the difference.
						</p>
						<div>
							<p>As your home base in New York, we’re here to help you:</p>
							<ul className="mt-2 list-disc space-y-1 pl-8">
								<li>Plan match days and transport</li>
								<li>Discover nearby dining and attractions</li>
								<li>Unwind in comfort after a high-energy night session</li>
							</ul>
						</div>
					</ArticleSection>

					<ArticleSection title="Make Your US Open Stay Memorable">
						<p>
							The US Open isn’t just a tennis tournament—it’s a New York summer tradition. If your August travels bring you to the city, we invite you to experience the matches, the atmosphere, and the unforgettable rhythm of New York, all while enjoying a stay designed around comfort and convenience.
						</p>
						<p>
							We look forward to welcoming you during one of the most exciting weeks of the year.{' '}
							<span aria-hidden="true">🎾🗽</span>
						</p>
					</ArticleSection>

					<section className="rounded-lg bg-[#171717] px-6 py-14 text-center text-white md:px-20">
							<h2 className="text-2xl font-semibold md:text-3xl">
								Ready to Experience US Open 2026?
							</h2>
							<p className="mx-auto mt-6 max-w-[760px] text-base leading-8 text-white/75 md:text-lg">
								Book your stay at Noctyra Hotel today and discover the city from a uniquely historical perspective.
							</p>
						<button
							type="button"
							className="mt-10 min-h-[70px] w-full max-w-[380px] rounded-lg bg-white px-10 text-xl font-semibold text-[#073033] transition-opacity hover:opacity-90"
						>
							Book Your Experience
						</button>
					</section>

					<div className="text-center">
						<Link
							to={`${HOTEL_ROUTE}#blogs`}
							className="inline-flex items-center gap-4 text-lg font-semibold text-[#073033] transition-opacity hover:opacity-75 md:text-xl"
						>
							<span aria-hidden="true">←</span>
							Back to All Articles
						</Link>
					</div>
				</article>
			</main>
			<NoctyraFooter />
			<NoctyraChatWidget isArticle />
		</div>
	);
};
